import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import usePopover, { panelStyle } from './usePopover'
import { kit } from './strings'
import { isEnglish } from '../lib/i18n'
import './DatePicker.css'

/*
 * Calendar labels come from Intl, not from a hand-written table: they are
 * locale data, not product copy.
 *
 * Both tags carry `-u-ca-gregory-nu-latn` on purpose. Plain `ar-SA` resolves to
 * the ISLAMIC calendar in Chrome, which would print a Hijri month over a
 * Gregorian ISO value, and to Arabic-Indic digits, which would not match the
 * Latin numerals every other figure in the app uses. The English tag is pinned
 * the same way so the two builds cannot drift apart on either count.
 */
const LOCALE = isEnglish ? 'en-GB-u-ca-gregory-nu-latn' : 'ar-u-ca-gregory-nu-latn'
const monthLabel = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' })

/*
 * The column heads are the SHORT day name, not the narrow one.
 *
 * `narrow` gives a single letter — S S M T W T F in English, س ح ن ث ر خ ج in
 * Arabic — and a single letter is ambiguous in both scripts: two S's, two T's,
 * and an Arabic set that is only readable if you already know the order. Every
 * calendar a user has met prints the three-letter form instead, so the grid is
 * scannable without counting columns.
 *
 * `short` is exactly that in English (Sat, Sun, Mon …). Arabic's short form
 * keeps the definite article — الأحد, الثلاثاء — which is the WHOLE word and
 * will not sit in a seven-column grid. Dropping the ال gives أحد / ثلاثاء, the
 * abbreviation Arabic calendars actually print, which is what this trims.
 */
const weekdayLabel = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' })
const weekdayText = (date) => weekdayLabel.format(date).replace(/^ال/, '')

/** The Saudi week starts on Saturday, which is JS weekday 6. */
const FIRST_WEEKDAY = 6

const iso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`

/** Parses `YYYY-MM-DD` as a LOCAL date — `new Date(iso)` would read it as UTC. */
const parse = (value) => {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  const date = new Date(y, m - 1, d)
  return Number.isNaN(date.getTime()) ? null : date
}

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)
const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1)
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** The six-week grid a month is drawn on, leading and trailing days included. */
function monthGrid(view) {
  const first = startOfMonth(view)
  const lead = (first.getDay() - FIRST_WEEKDAY + 7) % 7
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead)
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)
    return { day, outside: day.getMonth() !== view.getMonth() }
  })
}

/**
 * A date field with a calendar of our own.
 *
 * `<input type="date">` hands the picker to the OS: a Chrome-drawn panel in the
 * system font with a US-format mask, no way to style a day, and — on an
 * Arabic-first screen — month names and a week that start wherever the browser
 * decides. The field below keeps the app's own shell, and the panel is drawn in
 * the app's type, spacing and accent, with the week starting on Saturday.
 *
 * The value stays ISO `YYYY-MM-DD` in and out, so nothing that consumes it
 * changes.
 *
 * @param {string} value      ISO `YYYY-MM-DD`, empty for unset
 * @param {(value: string) => void} onChange
 * @param {string} [min] {string} [max]  ISO bounds; days outside are unpickable
 * @param {'field'|'bare'} [variant]  `bare` drops the shell, for a control that
 *   is already inside one (the two halves of DateRangeField)
 * @param {boolean} [clearable]  show a clear button once a date is set. OPT-IN,
 *   because for most fields "no date" is not a meaningful answer — but where the
 *   endpoint reads an empty value as an instruction of its own (a null unlocks an
 *   account, cancels a resignation, removes an expiry) the picker has to be able
 *   to get BACK to empty, which a calendar alone cannot do.
 */
export default function DatePicker({
  id,
  value,
  onChange,
  clearable = false,
  /* The FORMAT, not the field's name. Callers used to pass the label back in —
     `placeholder={S.form.startDate}` under a label already reading تاريخ
     البداية — which says the same thing twice and still leaves the reader
     guessing how the date should look. */
  placeholder = 'YYYY-MM-DD',
  min,
  max,
  disabled = false,
  /* Same reason as Select's: a required date that fails validation has to look
     failed, and without naming it here it reached the DOM as an attribute. */
  invalid = false,
  variant = 'field',
  className = '',
  ...rest
}) {
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({
    align: 'start',
  })

  const selected = useMemo(() => parse(value), [value])
  const today = useMemo(() => new Date(), [])
  const [view, setView] = useState(() => startOfMonth(selected ?? today))

  // Re-open on the month the value is in, not wherever it was left last time.
  useEffect(() => {
    if (open) setView(startOfMonth(selected ?? today))
  }, [open, selected, today])

  const lower = useMemo(() => parse(min), [min])
  const upper = useMemo(() => parse(max), [max])
  const blocked = (day) => (lower && day < lower) || (upper && day > upper)

  const days = useMemo(() => monthGrid(view), [view])
  /* 2024-01-06 IS a Saturday, so it is already column 0 — offsetting it by
     FIRST_WEEKDAY as well shifted every label one day off its column. */
  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => weekdayText(new Date(2024, 0, 6 + index))),
    [],
  )

  const take = (day) => {
    if (blocked(day)) return
    onChange?.(iso(day))
    close()
    rootRef.current?.querySelector('button')?.focus()
  }

  /* Sibling of the control, not a child of it: the control is itself a
     <button>, and a button inside a button is invalid markup that browsers
     silently un-nest. */
  const showClear = clearable && !!value && !disabled

  return (
    <div className={`datepicker datepicker--${variant} ${className}`.trim()} ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`datepicker__control${open ? ' is-open' : ''}${value ? '' : ' is-empty'}${
          invalid ? ' is-invalid' : ''
        }${showClear ? ' has-clear' : ''}`}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => setOpen((state) => !state)}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...rest}
      >
        {variant === 'field' && (
          <CalendarDays className="datepicker__icon" size={16} strokeWidth={1.75} aria-hidden="true" />
        )}
        <span className="datepicker__value" dir={value ? 'ltr' : undefined}>
          {value || placeholder}
        </span>
      </button>

      {showClear && (
        <button
          type="button"
          className="datepicker__clear"
          onClick={() => onChange?.('')}
          aria-label={kit.clearDate}
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      )}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={placeholder}
            className="datepicker__panel"
            style={panelStyle(pos)}
          >
            <div className="datepicker__head">
              {/* In RTL "previous" points right and "next" points left, so the
                  glyphs are chosen rather than mirrored. */}
              <button
                type="button"
                className="datepicker__nav"
                onClick={() => setView((month) => addMonths(month, -1))}
                aria-label={monthLabel.format(addMonths(view, -1))}
              >
                <ChevronRight className="ltr-flip" size={16} strokeWidth={2} />
              </button>
              <span className="datepicker__month">{monthLabel.format(view)}</span>
              <button
                type="button"
                className="datepicker__nav"
                onClick={() => setView((month) => addMonths(month, 1))}
                aria-label={monthLabel.format(addMonths(view, 1))}
              >
                <ChevronLeft className="ltr-flip" size={16} strokeWidth={2} />
              </button>
            </div>

            <div className="datepicker__weekdays" aria-hidden="true">
              {weekdays.map((label, index) => (
                <span key={index} className="datepicker__weekday">
                  {label}
                </span>
              ))}
            </div>

            <div className="datepicker__grid">
              {days.map(({ day, outside }) => {
                const isBlocked = blocked(day)
                return (
                  <button
                    key={iso(day)}
                    type="button"
                    className={`datepicker__day${outside ? ' is-outside' : ''}${
                      sameDay(day, selected) ? ' is-selected' : ''
                    }${sameDay(day, today) ? ' is-today' : ''}`}
                    disabled={isBlocked}
                    aria-current={sameDay(day, today) ? 'date' : undefined}
                    aria-pressed={sameDay(day, selected)}
                    onClick={() => take(day)}
                  >
                    {day.getDate()}
                  </button>
                )
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
